// ** React Imports
import { SyntheticEvent, useEffect, useState } from 'react'

// ** MUI Imports
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import Avatar from '@mui/material/Avatar'
import TabContext from '@mui/lab/TabContext'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import { Theme, useTheme } from '@mui/material/styles'

import { Icon } from '@iconify/react'
import { appColor, LemurContractFilterTabs } from '../../../common/constants'
import _ from 'lodash'
import LiquidityRiskDashboardGrid from '../../LiquidityRiskDashboardGrid'

type TabCategory = 'Price < 2' | 'ADTV 10 Days' | 'DTL > 2' | 'Stale Price' | 'Qty > 5% Float'

const LemurDashboardCCByContract = (props: any) => {
  // ** State
  const [currentTab, setCurrentTab] = useState<TabCategory>('Price < 2')

  // ** Hook
  const theme = useTheme()

  const [isChartVisible, setIsChartVisible] = useState<boolean>(true)

  const [tabData, setTabData] = useState<any>([
    {
      type: 'Price < 2',
      avatarIcon: 'tabler:shopping-cart',
      rowData: []
    },
    {
      type: 'Qty%ADTV > 10%',
      avatarIcon: 'tabler:chart-bar',
      rowData: []
    },
    {
      type: 'DTL > 2',
      avatarIcon: 'tabler:currency-dollar',
      rowData: []
    },
    {
      type: 'Stale Price',
      avatarIcon: 'tabler:currency-dollar',
      rowData: []
    },
    {
      type: 'Qty > 5% Float',
      avatarIcon: 'tabler:currency-dollar',
      rowData: []
    }
  ])

  useEffect(() => {
    const data = props.data
    if (!_.isEqual(data, tabData)) {
      const newTabData = [
        {
          type: LemurContractFilterTabs.priceLessThan2,
          avatarIcon: 'tabler:shopping-cart',
          rowData:
            data?.length > 0 ? data?.find((t: any) => t.type === LemurContractFilterTabs.priceLessThan2)?.rowData : []
        },
        {
          type: LemurContractFilterTabs.adtv10days,
          avatarIcon: 'tabler:chart-bar',
          rowData:
            data?.length > 0 ? data?.find((t: any) => t.type === LemurContractFilterTabs.adtv10days)?.rowData : []
        },
        {
          type: LemurContractFilterTabs.daysToLiq,
          avatarIcon: 'tabler:currency-dollar',
          rowData: data?.length > 0 ? data?.find((t: any) => t.type === LemurContractFilterTabs.daysToLiq)?.rowData : []
        },
        {
          type: LemurContractFilterTabs.stalePrice,
          avatarIcon: 'tabler:currency-dollar',
          rowData:
            data?.length > 0 ? data?.find((t: any) => t.type === LemurContractFilterTabs.stalePrice)?.rowData : []
        },
        {
          type: LemurContractFilterTabs.qtyGreaterThan5Float,
          avatarIcon: 'tabler:currency-dollar',
          rowData:
            data?.length > 0
              ? data?.find((t: any) => t.type === LemurContractFilterTabs.qtyGreaterThan5Float)?.rowData
              : []
        }
      ]
      setTabData(newTabData)
    }
  }, [props.data])

  useEffect(() => {
    setIsChartVisible(false)
  }, [tabData])

  useEffect(() => {
    if (!isChartVisible) setIsChartVisible(true)
  }, [isChartVisible])

  useEffect(() => {
    renderTabPanels()
  }, [currentTab, tabData])

  const renderTabs = (value: TabCategory, theme: Theme) => {
    return tabData.map((item: any, index: any) => {
      const RenderAvatar = item.type === value ? Avatar : Avatar

      return (
        <Tab
          key={index}
          value={item.type}
          className='pt-0 me-2'
          label={
            <Box
              sx={{
                borderWidth: 1,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '10px',
                flexDirection: 'row',
                justifyContent: 'center',
                borderStyle: 'solid',
                borderColor: item.type === value ? `${appColor}` : theme.palette.divider,
                backgroundColor: item.type === value ? `${appColor}` : theme.palette.common.white
              }}
            >
              <RenderAvatar
                variant='rounded'
                {...(item.type === value && { skin: 'light' })}
                sx={{
                  width: 34,
                  height: 34,
                  ...(item.type !== value && { backgroundColor: 'action.selected' }),
                  color: `${`${item.type !== value ? `` : `${theme.palette.common.white} !important`}`}`
                }}
              >
                <Icon
                  icon={item.avatarIcon}
                  color={`${item.type !== value ? `` : `${theme.palette.common.white} !important`}`}
                  style={{
                    borderColor: `${`${item.type !== value ? `` : `${theme.palette.common.white} !important`}`}`
                  }}
                />
              </RenderAvatar>
              <Typography
                sx={{
                  paddingX: 1,
                  fontWeight: 500,
                  color: `${
                    item.type === value ? `${theme.palette.common.white} !important` : `${appColor}`
                  }`,
                  textTransform: 'capitalize'
                }}
              >
                {item.type}
              </Typography>
            </Box>
          }
        />
      )
    })
  }

  const renderTabPanels = () => {
    return tabData.map((item: any, index: any) => {
      return (
        <TabPanel className='p-0' key={index} value={item.type} sx={{ height: '22vh !important' }}>
          <LiquidityRiskDashboardGrid
            rowData={item?.rowData}
            getGridRef={() => {
              /* */
            }}
            isByContractsGrid={true}
          />
        </TabPanel>
      )
    })
  }

  const handleChange = (event: SyntheticEvent, newValue: TabCategory) => {
    setCurrentTab(newValue)
  }

  return (
    <Card className='h-100 w-100'>
      <CardContent className='h-100 p-0' sx={{ '& .MuiTabPanel-root': { p: 0 } }}>
        <TabContext value={currentTab}>
          <TabList
            variant='scrollable'
            scrollButtons='auto'
            className='p-0 sx-md-overflow-x'
            id='locate-bar-chart'
            onChange={handleChange}
            aria-label='earning report tabs'
            sx={{
              overflowX: 'auto',
              border: '0 !important',
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': { p: 0, minWidth: 0, borderRadius: '10px', '&:not(:last-child)': { mr: 4 } }
            }}
          >
            {renderTabs(currentTab, theme)}
          </TabList>
          {renderTabPanels()}
        </TabContext>
      </CardContent>
    </Card>
  )
}

export default LemurDashboardCCByContract
